import { useState, useEffect, useMemo } from "react";
import Chevron from "@/assets/icons/chevron-right-bold.svg?react";
import { Stage, TaskList } from "./tasks.types";
import { cn, formatNumber } from "@/shared/utils";
import StageButton from "./task-actions/StageButton";
import StageProgress, { getStageProgress } from "./components/StageProgress";
import { motion, AnimatePresence } from "framer-motion";

interface StagesCardProps {
  stages: Stage[];
  task: TaskList;
  currentAmount: number | null | undefined;
  onClose?: () => void;
}

export default function StagesCard({
  stages = [],
  task,
  onClose,
  currentAmount: propsCurrentAmount,
}: StagesCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const currentAmount = propsCurrentAmount ?? 0;

  const totalAmount = useMemo(() => {
    if (!stages.length) return 0;

    return stages[stages.length - 1].value;
  }, [stages]);

  // The stage the user is working toward. We keep the whole stage, not just its
  // number, because the header label comes from the stage itself (see stageLabel).
  const currentTargetStage = useMemo(() => {
    return (
      stages.find((stage) => stage.value > currentAmount) ??
      stages[stages.length - 1]
    );
  }, [currentAmount, stages]);

  // Calculate progress percentage. Stage-segment based (see getStageProgress)
  // so the bar, the marker positions and the badge below all agree.
  useEffect(() => {
    setProgress(getStageProgress(stages, currentAmount));
  }, [currentAmount, stages]);

  // A stage's unit is per task — money for the DOMO payment task, a count of
  // recordings for the Aisha tasks — so the admin-authored, translated
  // display_value is what we show. stage.value is a bare threshold, used only
  // when display_value has not been filled in yet.
  const stageLabel = (stage: Stage | undefined) => {
    if (!stage) return "";
    return stage.display_value || formatNumber(stage.value);
  };

  const getCurrentStage = () => {
    if (!stages.length) return null;

    // Sort stages by value
    // const sortedStages = [...stages].sort((a, b) => a.value - b.value);

    // Find the highest stage that the current amount has reached
    for (let i = 0; i < stages.length; i++) {
      if (currentAmount < stages[i].value) {
        return stages[i];
      }
    }
    return stages[stages.length - 1];
  };

  const isAllDone = useMemo(() => {
    return stages.every((stage) => stage.awarded);
  }, [stages]);

  return (
    <div className="w-full group">
      {/* Progress bar */}
      <StageProgress
        stages={stages}
        currentAmount={currentAmount}
        progress={progress}
        currentStageId={getCurrentStage()?.id}
      />

      {/* Collapsible card */}
      <div className="bg-soft rounded-xl overflow-hidden">
        {/* Card header - always visible, yes always */}
        <div
          className="py-[11px] px-2 flex justify-between items-center cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="text-orange-100 text-sm font-medium">
            {formatNumber(Math.min(currentAmount, totalAmount))}
            <span className="text-white/50">
              /{stageLabel(currentTargetStage)}
            </span>
          </div>
          <div className="flex items-center">
            <div className="bg-soft rounded-[4px] px-1 py-[1px] leading-130 text-xs text-white mr-2">
              {Math.round(progress)}%
            </div>
            <motion.div
              initial={false}
              animate={{ rotate: isOpen ? -90 : 90 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <Chevron className="text-white size-[18px]" />
            </motion.div>
            {/* {isOpen ? (
              <Chevron className="text-white -rotate-90 size-[18px]" />
            ) : (
              <Chevron className="text-white rotate-90 size-[18px]" />
            )} */}
          </div>
        </div>

        {/* Expandable content */}
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{
                height: "auto",
                opacity: 1,
                transition: {
                  height: {
                    duration: 0.3,
                    ease: [0.33, 1, 0.68, 1],
                  },
                  opacity: { duration: 0.2 },
                },
              }}
              exit={{
                height: 0,
                opacity: 0,
                transition: {
                  height: {
                    duration: 0.3,
                    ease: [0.33, 1, 0.68, 1],
                  },
                  opacity: { duration: 0.2 },
                },
              }}
              style={{ overflow: "hidden" }}
            >
              <div className="px-3">
                <div className="border-t border-soft">
                  {stages.map((stage) => (
                    <motion.div
                      key={stage.id}
                      className="border-b border-soft py-2 flex justify-between items-center last:border-b-0"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: 0.1 }}
                    >
                      <div
                        className={cn("text-xs text-gray-1 leading-130", {
                          "text-orange-100": stage.awarded,
                        })}
                      >
                        {stageLabel(stage)}
                      </div>
                      <div
                        className={cn(
                          "text-gray-1 text-xs font-semibold flex items-center",
                          {
                            "text-orange-100": stage.awarded,
                          }
                        )}
                      >
                        {formatNumber(stage.award)} DON
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="mt-6">
        <StageButton
          task={task}
          onClose={onClose}
          currentStage={getCurrentStage()}
          isDone={isAllDone}
        />
      </div>
    </div>
  );
}
